import { useEffect, useMemo, useRef, useState } from 'react';
import type { AppState, Can } from '../../shared/api';
import { CanSearch } from '../components/CanSearch';
import { Sheet } from '../components/Sheet';
import { ApiError, useAddCan, useUploadCanImage } from '../api';

interface LinkProps {
  state: AppState;
  ean: string;
  /** What Open Food Facts calls the product, to start the search with. */
  suggestion?: string | null;
  onPick: (canId: number) => void;
  onNewFlavor: () => void;
  onClose: () => void;
}

/** Unknown EAN: pick the can it belongs to, or add a new flavor. */
export function LinkBarcodeSheet({ state, ean, suggestion, onPick, onNewFlavor, onClose }: LinkProps) {
  const owned = useMemo(() => new Set(state.entries.map((e) => e.canId)), [state.entries]);
  const initialQuery = useMemo(() => cleanSuggestion(suggestion), [suggestion]);

  return (
    <Sheet label="Onbekende streepjescode" onClose={onClose}>
      {(close) => (
        <>
          <div className="label label--green">Gescand · onbekend</div>
          <h2 className="sheet__name">Welk blik is dit?</h2>
          <div className="note">
            EAN <span style={{ fontFamily: 'var(--mono)' }}>{ean}</span> kennen we nog niet
            {suggestion ? (
              <>
                {' '}
                (Open Food Facts noemt hem <q>{suggestion}</q>)
              </>
            ) : null}
            . Kies het blik; de volgende scan herkent hem meteen.
          </div>
          <div style={{ marginTop: 16 }}>
            <CanSearch
              cans={state.cans}
              categories={state.categories}
              initialQuery={initialQuery}
              autoFocus
              meta={(c) => (owned.has(c.id) ? '✓' : null)}
              empty={<div className="pick-list__empty">Typ een paar letters van de naam.</div>}
              onPick={(can: Can) => {
                close();
                onPick(can.id);
              }}
            />
          </div>
          <button
            type="button"
            className="btn btn--outline"
            style={{ marginTop: 16, width: '100%' }}
            onClick={() => {
              close();
              onNewFlavor();
            }}
          >
            Nieuwe smaak toevoegen
          </button>
        </>
      )}
    </Sheet>
  );
}

/** "Monster Energy Drink Ultra Rosa 500ml" → "Ultra Rosa": only the words that name the flavor. */
function cleanSuggestion(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .replace(/\d+(?:[.,]\d+)?\s*(?:ml|cl|l|fl\.?\s*oz|oz)\b/gi, ' ')
    .replace(/\b(monster|energy|energie|drink|boisson|energisante|bebida|the)\b/gi, ' ')
    .replace(/[^\p{L}\p{N}' ]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface NewFlavorProps {
  state: AppState;
  ean?: string;
  /** Where the new flavor goes next: straight to the wall, or onto the zoeklijst. */
  target: 'wall' | 'wanted';
  onCreated: (can: Can) => void;
  onClose: () => void;
}

/** Grows the own catalog with a flavor, optionally with a photo of the can. */
export function NewFlavorSheet({ state, ean, target, onCreated, onClose }: NewFlavorProps) {
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState(state.categories[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Set once POST /api/cans succeeded, so a retry after a failed upload never creates the flavor twice.
  const [created, setCreated] = useState<Can | null>(null);
  const [phase, setPhase] = useState<'creating' | 'uploading' | null>(null);
  const addCan = useAddCan();
  const uploadImage = useUploadCanImage();
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const busy = phase !== null;

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked) {
      setFile(picked);
      setError(null);
    }
    e.target.value = '';
  };

  const submit = async (close: () => void) => {
    setError(null);
    let can = created;
    if (!can) {
      setPhase('creating');
      try {
        can = await addCan.mutateAsync({ name: name.trim(), categoryId, wanted: target === 'wanted' });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Toevoegen lukte niet.');
        setPhase(null);
        return;
      }
      setCreated(can);
    }
    let imageId = can.imageId;
    if (file) {
      setPhase('uploading');
      try {
        imageId = (await uploadImage.mutateAsync({ id: can.id, file })).imageId;
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Uploaden lukte niet.');
        setPhase(null);
        return;
      }
    }
    close();
    onCreated({ ...can, imageId });
  };

  return (
    <Sheet label="Nieuwe smaak" onClose={onClose}>
      {(close) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit(close);
          }}
        >
          <div className="label label--green">{ean ? 'Gescand · nieuwe smaak' : 'Eigen catalogus'}</div>
          <h2 className="sheet__name">Nieuwe smaak</h2>
          <label className="field" style={{ marginTop: 18 }}>
            <span className="label">Naam</span>
            <input
              className="input"
              style={{ marginTop: 10 }}
              data-autofocus
              required
              maxLength={120}
              placeholder="bijv. Ultra Strawberry Dreams"
              value={name}
              disabled={created !== null}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="label section-label">Reeks</div>
          <div className="countries">
            {state.categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className="chip country-chip"
                aria-pressed={categoryId === c.id}
                disabled={created !== null}
                onClick={() => setCategoryId(c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="label section-label">Foto (optioneel)</div>
          {previewUrl && (
            <div className="photo-preview">
              <img className="photo-preview__img" src={previewUrl} alt={`Gekozen foto van ${name.trim() || 'het nieuwe blik'}`} />
              <button type="button" className="btn btn--outline photo-preview__remove" disabled={busy} onClick={() => setFile(null)}>
                Foto verwijderen
              </button>
            </div>
          )}
          <div className="photo-pick">
            <button type="button" className="btn btn--outline" disabled={busy} onClick={() => cameraInput.current?.click()}>
              Foto maken
            </button>
            <button type="button" className="btn btn--outline" disabled={busy} onClick={() => fileInput.current?.click()}>
              Afbeelding kiezen
            </button>
          </div>
          <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={pickFile} />
          <input ref={fileInput} type="file" accept="image/*" hidden onChange={pickFile} />
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          <button type="submit" className="btn" style={{ marginTop: 24 }} disabled={!name.trim() || busy}>
            {phase === 'creating'
              ? 'Bezig…'
              : phase === 'uploading'
                ? 'Uploaden…'
                : created
                  ? file
                    ? 'Opnieuw uploaden'
                    : 'Doorgaan'
                  : target === 'wall'
                    ? 'Toevoegen en kiezen'
                    : 'Op de zoeklijst'}
          </button>
          {created && file && (
            <button
              type="button"
              className="btn btn--outline"
              style={{ marginTop: 10 }}
              disabled={busy}
              onClick={() => {
                close();
                onCreated(created);
              }}
            >
              Doorgaan zonder foto
            </button>
          )}
        </form>
      )}
    </Sheet>
  );
}
